import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setLoginModal, setLoginTab, setLoginCallLocation } from '@/redux/login/loginSlice';
import { saveUserSmsOptInFlag, getUserProfile } from '@/api/myProfile';
import { postSmsFlag, updateSmsWatchList } from '@/api/PDP';
import { setProfileInfo } from '@/redux/user/userSlice';
import { trackButtonClick } from '@/utils/analytics';
import RegisterTourModal from '../RegisterTourModal';
import './commercial-description.css';

/**
 * PropertySidebar - Right sidebar with Receive Alerts, Property Tour,
 * Occupied warning, and Important Property Information cards.
 */
const PropertySidebar = ({ openTourModalOnLoad, onTourModalOnLoadConsumed, onRegisterTourRequested }) => {
  const dispatch = useDispatch();
  const pdpInitData = useSelector((state) => state.pdp?.pdpInitData);
  const user = useSelector((state) => state.user?.user);
  const isUserLoggedIn = useSelector((state) => state.user.isUserLoggedIn);
  const otpModalOpen = useSelector((state) => state.otpModal?.showOtpModal);

  const propertyDetails = pdpInitData?.propertyDetails;
  const commonAttributes = propertyDetails?.commonAttributes;
  const listingDetail = pdpInitData?.listingDetail;
  const propertySpecialDetails = pdpInitData?.propertySpecialDetails;
  const propertyId = propertyDetails?.propertyId;

  const smsOptInStatus = user?.smsOptInFlag;
  const isForeclosure = commonAttributes?.propCategory === 'FORECLOSURE';
  const propertyWatchStatus = propertySpecialDetails?.smsWatchListStatus === 'Y';

  // tourPreFilled is a root-level field on the getNewPdpDetails API response (pdpInitData.tourPreFilled).
  // true  = user already registered → show "Registered" confirmation card
  // false/absent = not yet registered → show Register CTA
  const alreadyRegistered = pdpInitData?.tourPreFilled === true;

  // Set to true after a successful in-session submission so the card flips to
  // "Registered" state immediately without waiting for a page reload.
  const [tourSubmitted, setTourSubmitted] = useState(false);

  const isRegistered = alreadyRegistered || tourSubmitted;

  // Open modal immediately on mount if the page signals a deferred open (post-login remount).
  const [showTourModal, setShowTourModal] = useState(() => {
    if (openTourModalOnLoad && isUserLoggedIn && !otpModalOpen) {
      return true;
    }
    return false;
  });

  // Clear the page-level flag after mount if we consumed it to open the modal
  useEffect(() => {
    if (showTourModal && openTourModalOnLoad) {
      onTourModalOnLoadConsumed?.();
    }
  }, []);

  // Refs for detecting login transition and focus management
  const prevLoggedInRef = useRef(isUserLoggedIn);
  const registerBtnRef = useRef(null);

  // Toggle state for the alerts switch
  const [toggle, setToggle] = useState(
    isForeclosure ? propertyWatchStatus : smsOptInStatus === 'Y'
  );
  const [phoneNo, setPhoneNo] = useState(user?.primaryPhone || '');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(smsOptInStatus);

  // Sync toggle when smsOptInStatus changes
  useEffect(() => {
    if (smsOptInStatus === 'Y') {
      setToggle(true);
    } else if (smsOptInStatus === 'N') {
      setToggle(false);
    }
  }, [smsOptInStatus]);

  // Reset toggle when user logs out; restore on login
  useEffect(() => {
    if (!isUserLoggedIn) {
      setToggle(false);
      setSmsOptIn(smsOptInStatus);
    } else if (isUserLoggedIn && isForeclosure) {
      setToggle(propertyWatchStatus);
    } else if (isUserLoggedIn && smsOptInStatus === 'Y') {
      setToggle(true);
    }
  }, [isUserLoggedIn, propertyWatchStatus, smsOptInStatus, isForeclosure]);

  // Update phoneNo when user profile loads
  useEffect(() => {
    if (user?.primaryPhone) {
      setPhoneNo(user.primaryPhone);
    }
  }, [user?.primaryPhone]);

  // Open tour modal after login if the user clicked Register while logged out.
  // prevLoggedInRef detects the false→true transition on the currently mounted instance.
  // The lazy useState initializer handles the case where the component remounts (shimmer re-fetch).
  useEffect(() => {
    const wasLoggedOut = !prevLoggedInRef.current;
    prevLoggedInRef.current = isUserLoggedIn;
    if (isUserLoggedIn && wasLoggedOut && openTourModalOnLoad) {
      if (!otpModalOpen) {
        setShowTourModal(true);
      }
    }
  }, [isUserLoggedIn]);

  // If OTP was open when login completed, open tour modal once OTP closes.
  useEffect(() => {
    if (!otpModalOpen && openTourModalOnLoad && isUserLoggedIn) {
      setShowTourModal(true);
    }
  }, [otpModalOpen]);

  const isLoggedIn = user && Object.keys(user).length > 0;

  const isOccupied = commonAttributes?.isOccupied === 'Y' || commonAttributes?.isOccupied === true;
  const financialType = listingDetail?.financialType || [];
  const commercialFeatures = propertySpecialDetails?.commercialPropertyAdditionalFeatures;

  // Map from commercialPropertyAdditionalFeatures (primary) with fallback to listingDetail
  const hasAssumableFinancing = commercialFeatures?.assumableDebt === true;
  const hasBrokerCoOp = commercialFeatures?.brokerCoopAvailable === true;
  const hasDiscountedFee = commercialFeatures?.discountedTransactionFee === true;

  // Important Property Information cards
  const infoCards = [];
  if (hasAssumableFinancing) {
    infoCards.push({
      iconSrc: '/assets/images/pdp/AssumableFinancing.png',
      iconClass: 'commercial-sidebar__info-card-icon--red',
      borderColor: '#F25D49',
      title: 'Assumable Financing',
      description: "Buyers may be able to take over the seller's existing loan, subject to lender approval and loan terms.",
    });
  }
  if (hasBrokerCoOp) {
    infoCards.push({
      iconSrc: '/assets/images/pdp/BrokerCo-OpAvailable.png',
      iconClass: 'commercial-sidebar__info-card-icon--blue',
      borderColor: '#118BF4',
      title: 'Broker Co-Op Available',
      description: 'A commission may be offered to a licensed broker or agent who represents the buyer in the transaction.',
    });
  }
  if (hasDiscountedFee) {
    infoCards.push({
      iconSrc: '/assets/images/pdp/DiscountedTransactionFee.png',
      iconClass: 'commercial-sidebar__info-card-icon--navy',
      borderColor: '#254A72',
      title: 'Discounted Transaction Fee',
      description: 'Buyers may pay a reduced transaction fee compared with the standard fee for this sale.',
    });
  }
  if (commercialFeatures?.sellerFinancingAvailable === true || commercialFeatures?.sellerFinancingAvailable === 'Y') {
    infoCards.push({
      iconSrc: '/assets/images/pdp/SellerFinancingAvailable.png',
      iconClass: 'commercial-sidebar__info-card-icon--green',
      borderColor: '#3FB035',
      title: 'Seller Financing Available',
      description: 'The seller may offer financing options to qualified buyers, subject to agreed-upon terms.',
    });
  }
  if (commercialFeatures?.soldSubjectToFinalApproval === true || commercialFeatures?.soldSubjectToFinalApproval === 'Y') {
    infoCards.push({
      iconSrc: '/assets/images/pdp/SoldSubjecttoFinalApproval.png',
      iconClass: 'commercial-sidebar__info-card-icon--orange',
      borderColor: '#FF7900',
      title: 'Sold Subject to Final Approval',
      description: 'Buyers may pay a reduced transaction fee compared with the standard fee for this sale.',
    });
  }

  const validatePhone = (text) => {
    const number = text || phoneNo || user?.primaryPhone;
    const format = /^\d{10}$/;
    if (!number || number.length === 0) {
      setPhoneNumberError('This field is required');
      return true;
    }
    if (!format.test(number)) {
      setPhoneNumberError('Invalid Phone Number');
      return true;
    }
    setPhoneNumberError('');
    return false;
  };

  const handleAlertsToggle = (e) => {
    if (!isLoggedIn) {
      dispatch(setLoginModal(true));
      dispatch(setLoginTab('loginTab'));
      dispatch(setLoginCallLocation('Popup'));
      return;
    }

    const checked = e.target.checked;
    setToggle(checked);

    trackButtonClick({
      eventName: 'opt_for_sms_toggle',
      eventParams: { opt_status: checked ? 'in' : 'out' },
      eventCategory: 'COMMERCIAL-PDP',
      eventSource: 'on toggle button click',
    });

    // If already opted in, handle turn on/off directly
    if (smsOptInStatus === 'Y' || smsOptIn === 'Y') {
      if (checked) {
        saveUserSmsOptInFlag({ phoneNumber: user.primaryPhone, smsOptIn: 'Y' }).then((response) => {
          if (response?.status === 'SUCCESS') {
            setSmsOptIn('Y');
            getUserProfile().then((res) => dispatch(setProfileInfo(res?.data)));
          }
        });
        if (isForeclosure) {
          updateSmsWatchList({ propertyId, updateFlag: 'Y' });
        }
      } else {
        if (isForeclosure) {
          updateSmsWatchList({ propertyId, updateFlag: 'N' });
        } else {
          saveUserSmsOptInFlag({ phoneNumber: user.primaryPhone, smsOptIn: 'N' }).then(() => {
            getUserProfile().then((res) => dispatch(setProfileInfo(res?.data)));
          });
        }
      }
    }
  };

  const handleSavePhone = () => {
    const isError = validatePhone();
    if (isError) return;

    saveUserSmsOptInFlag({ phoneNumber: phoneNo, smsOptIn: 'Y' }).then((response) => {
      if (response?.status === 'SUCCESS') {
        setSmsOptIn('Y');
        getUserProfile().then((res) => dispatch(setProfileInfo(res?.data)));
      }
    });

    if (isForeclosure) {
      updateSmsWatchList({ propertyId, updateFlag: 'Y' });
    }

    trackButtonClick({
      eventName: 'opt_in_for_sms_pdp_saved',
      eventCategory: 'COMMERCIAL-PDP',
      eventSource: 'on save button click',
    });
  };

  const handleRegisterClick = () => {
    if (!isLoggedIn) {
      onRegisterTourRequested?.();
      dispatch(setLoginModal(true));
      dispatch(setLoginTab('loginTab'));
      dispatch(setLoginCallLocation('Popup'));
      return;
    }
    trackButtonClick({
      pageName: 'Register for Property Tour - CTA Clicked',
      eventCategory: 'COMMERCIAL-PDP',
      eventSource: 'on register button click',
    });
    setShowTourModal(true);
  };

  return (
    <div className="commercial-sidebar">
      {/* Receive Alerts — hidden when already opted in */}
      {smsOptInStatus !== 'Y' && (
        <div className="commercial-sidebar__card">
          <div className="commercial-sidebar__card-row">
            <div>
              <div className="commercial-sidebar__card-title">Receive Alerts</div>
              <div className="commercial-sidebar__card-subtitle">Stay up-to-date on this property.</div>
            </div>
            <div className="commercial-sidebar__toggle" role="switch" aria-checked={toggle} aria-label="Receive property alerts" onClick={handleAlertsToggle} onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleAlertsToggle(); } }} tabIndex={0}>
              <input type="checkbox" checked={toggle} onChange={() => {}} aria-hidden="true" tabIndex={-1} aria-label="Receive property alerts" />
              <span className="commercial-sidebar__toggle-slider" aria-hidden="true" aria-label="Receive property alerts"/>
            </div>
          </div>

          {/* Phone input — shown when toggled ON but SMS not yet opted in */}
          {toggle && smsOptIn !== 'Y' && isLoggedIn && (
            <div className="commercial-sidebar__phone-row">
              <input
                className="commercial-sidebar__phone-input"
                placeholder="Enter your phone number"
                aria-label="Phone number for alerts"
                aria-invalid={!!phoneNumberError}
                aria-describedby={phoneNumberError ? "phone-error-msg" : undefined}
                maxLength={10}
                value={phoneNo}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 10) setPhoneNo(value);
                }}
                onBlur={() => validatePhone()}
              />
              <button
                className="commercial-sidebar__phone-save-btn"
                type="button"
                onClick={handleSavePhone}
              >
                Save
              </button>
              {phoneNumberError && (
                <div className="commercial-sidebar__phone-error" id="phone-error-msg" role="alert">{phoneNumberError}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Register for Property Tour — always visible; flips to confirmed state when registered */}
      {isRegistered ? (
        <div className="commercial-sidebar__tour-registered" role="status" aria-label="Registered for Property Tour">
          <span className="commercial-sidebar__registered-icon" aria-hidden="true">✓</span>
          <span className="commercial-sidebar__tour-registered-text">Registered for Property Tour</span>
        </div>
      ) : (
        <div className="commercial-sidebar__card">
          <div className="commercial-sidebar__card-row">
            <div className="commercial-sidebar__card-title">Register for Property Tour</div>
            <button
              ref={registerBtnRef}
              type="button"
              className="commercial-sidebar__register-btn"
              onClick={handleRegisterClick}
              aria-label="Register for a property tour"
            >
              Register
            </button>
          </div>
        </div>
      )}

      {/* Tour registration modal */}
      {!isRegistered && (
        <RegisterTourModal
          show={showTourModal}
          onHide={() => { onTourModalOnLoadConsumed?.(); setShowTourModal(false); }}
          onSuccess={() => {
            onTourModalOnLoadConsumed?.();
            setTourSubmitted(true);
            setShowTourModal(false);
          }}
          user={user}
          propertyId={propertyId}
          address={propertyDetails?.address}
        />
      )}

      {/* Occupied - Do Not Disturb */}
      {isOccupied && (
        <div className="commercial-sidebar__card commercial-sidebar__card--warning">
          <div className="commercial-sidebar__card-row">
            <img height={32} width={32} src="/assets/images/pdp/occupied.png" alt="" className="commercial-sidebar__warning-icon" />
            <div>
              <div className="commercial-sidebar__card-title">Occupied - Do Not Disturb</div>
              <div className="commercial-sidebar__card-subtitle">
                Attempting to inspect or visit an occupied property may be considered trespassing.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Important Property Information */}
      {infoCards.length > 0 && (
        <div className="commercial-sidebar__info-section">
          <h3 className="commercial-sidebar__info-heading">Important Property Information</h3>
          {infoCards.map((card, index) => (
            <div
              key={index}
              className="commercial-sidebar__info-card"
              style={{ borderLeftColor: card.borderColor }}
            >
              <div className={`commercial-sidebar__info-card-icon ${card.iconClass}`}>
                <img src={card.iconSrc} alt="" className="commercial-sidebar__info-card-icon-img" />
              </div>
              <div className="commercial-sidebar__info-card-content">
                <div className="commercial-sidebar__info-card-title">{card.title}</div>
                <div className="commercial-sidebar__info-card-desc">{card.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertySidebar;
